import type { RuleSet, RuleTreeNode } from "./storeRules";
import type { SkuAssignment } from "./studioTypes";
import { storeRulesDisagreement, type ShippedCdt } from "./storeRulesAgreement";

const pairKey = (cdt0: string, cdt1: string) => JSON.stringify([cdt0, cdt1]);

const treeOf = (items: readonly SkuAssignment[], tier: 2 | 3): RuleTreeNode => {
  const groups = new Map<string, SkuAssignment[]>();
  for (const item of items) {
    const value = tier === 2 ? item.cdt2 : item.cdt3;
    const group = groups.get(value) ?? [];
    group.push(item);
    groups.set(value, group);
  }
  return {
    attr: `__cdt${tier}`,
    carrier: tier === 2 ? "cdt2" : "lossy",
    describe: `Studio CDT${tier} order`,
    direction: "ascending",
    kids: [...groups].map(([value, group]) => ({
      value,
      count: group.length,
      node: tier === 2 ? treeOf(group, 3) : null,
    })),
  };
};

export const compressedStoreRules = (measured: RuleSet, shipped: ShippedCdt): RuleSet => {
  const disagreement = storeRulesDisagreement(measured, shipped);
  if (disagreement !== null) throw new Error(disagreement);
  const factsOf = new Map(measured.skus.map((facts) => [facts.skuId, facts]));
  const ordered = [...shipped.assignments].sort(
    (a, b) =>
      shipped.cdt0Order.indexOf(a.cdt0) - shipped.cdt0Order.indexOf(b.cdt0) ||
      a.cdt1Priority - b.cdt1Priority ||
      a.cdt2Priority - b.cdt2Priority ||
      a.cdt3Priority - b.cdt3Priority,
  );
  const groups = new Map<string, { cdt0: string; cdt1: string; items: SkuAssignment[] }>();
  const skus = ordered.map((item, walkRank) => {
    const facts = factsOf.get(item.skuId);
    if (!facts) throw new Error(`Missing store facts for ${item.skuId}`);
    const key = pairKey(facts.cdt0, item.cdt1);
    const group = groups.get(key) ?? { cdt0: facts.cdt0, cdt1: item.cdt1, items: [] };
    group.items.push(item);
    groups.set(key, group);
    return {
      ...facts,
      cdt1: item.cdt1,
      walkRank,
      attrs: { ...facts.attrs, __cdt1: item.cdt1, __cdt2: item.cdt2, __cdt3: item.cdt3 },
    };
  });
  return {
    ...measured,
    skus,
    cdt1AttrOfCdt0: Object.fromEntries(skus.map((sku) => [sku.cdt0, "__cdt1"])),
    pairOrder: [...groups.values()].map(({ cdt0, cdt1 }) => ({ cdt0, cdt1 })),
    nodes: [...groups.values()].map(({ cdt0, cdt1, items }) => ({
      cdt0,
      cdt1,
      tree: treeOf(items, 2),
    })),
    // Pools inferred from the old hierarchy cannot describe the compressed groups.
    basePools: [],
  };
};

export const compressedRulesDisagreement = (rules: RuleSet, shipped: ShippedCdt): string | null => {
  const expected = compressedStoreRules(rules, shipped);
  if (
    new Set(rules.skus.map((sku) => sku.skuId)).size !== shipped.assignments.length ||
    rules.skus.length !== shipped.assignments.length
  )
    return "The store rules have different product coverage";
  for (const facts of expected.skus) {
    const actual = rules.skus.find((sku) => sku.skuId === facts.skuId);
    if (
      actual?.cdt1 !== facts.cdt1 ||
      ["__cdt1", "__cdt2", "__cdt3"].some((attr) => actual?.attrs[attr] !== facts.attrs[attr])
    )
      return `Product ${facts.skuId} has different compressed CDT labels in the store rules`;
  }
  if (JSON.stringify(rules.pairOrder) !== JSON.stringify(expected.pairOrder))
    return "The store rules have a different CDT1 order";
  if (JSON.stringify(rules.nodes) !== JSON.stringify(expected.nodes))
    return "The store rules have a different compressed hierarchy or order";
  return null;
};
